import AdminContextMenu from "components/admin/AdminContextMenu";
import PageLoader from "components/general/PageLoader";
import { Box, Link, Pressable, Text } from "components/ui";
import CheckBox from "expo-checkbox";
import { ChevronRight, MapPin } from "lucide-react-native";
import React, { useEffect } from "react";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { TreeNode, useGetGroupQuery } from "store/abcoApi";
import { truncateText } from "utils/globalFunctions";
import { useIsMobile } from "utils/hooks/useIsMobile";

interface TreeGroupNodeProps {
	basicNode: TreeNode;
	level: number;
	defaultOpen: boolean;
	render?: (node: TreeNode) => React.ReactNode;
	variant?: string;
	isFluid?: boolean;
	onSelect?: (node: TreeNode) => void;
	includeContextMenu?: boolean;
	selectMode?: "None" | "Multiple";
	allowedTypes?: "Both" | "Site" | "Group";
	value?: TreeNode | TreeNode[];
	onMultipleSelect?: (node: TreeNode) => void;
	selectedElementRef?: React.RefObject<any>;
	scrollToElement?: () => void;
	matchMode?: "Site" | "Group";
	truncateTextSize?: number;
	onContextMenuPress?: (action: string, node: TreeNode) => void;
	rootNodeName?: string;
}

const useRotationAnimation = (isOpen: boolean) => {
	const rotation = useSharedValue(0);

	useEffect(() => {
		rotation.value = isOpen ? withTiming(90, { duration: 200 }) : withTiming(0, { duration: 200 });
	}, [isOpen]);

	return useAnimatedStyle(() => ({
		transform: [{ rotateZ: `${rotation.value}deg` }],
	}));
};

const isSelectedNodeInChildren = (node: TreeNode, selectedIds: string[], matchMode: "Site" | "Group"): boolean => {
	if (!node || !node.children) return false;

	for (const child of node.children) {
		if (selectedIds.some((id) => (matchMode === "Site" && child.siteId === id) || (matchMode === "Group" && child.id === id))) {
			return true;
		}
		if (isSelectedNodeInChildren(child, selectedIds, matchMode)) return true;
	}

	return false;
};

const useInitialSelectionState = (basicNode: TreeNode, value?: TreeNode | TreeNode[], matchMode?: "Site" | "Group"): [boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
	return React.useState(() => {
		if (basicNode && value) {
			const valuesArray = Array.isArray(value) ? value : [value];
			return valuesArray.some((val) => (matchMode === "Site" && basicNode.siteId === val.id) || (matchMode === "Group" && basicNode.id === val.id));
		}
		return false;
	});
};

const ChevronToggle: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
	const animatedStyle = useRotationAnimation(isOpen);
	return (
		<Animated.View style={animatedStyle}>
			<ChevronRight color="black" size={32} />
		</Animated.View>
	);
};

interface NodeTextContainerProps {
	children: React.ReactNode;
	basicNode: TreeNode;
	selectMode: "None" | "Multiple";
	allowedTypes: "Both" | "Site" | "Group";
	handlePress: () => void;
	onSelect?: (node: TreeNode) => void;
	handleOnChecked: (node: TreeNode) => void;
}

const NodeTextContainer: React.FC<NodeTextContainerProps> = ({ children, basicNode, selectMode, allowedTypes, handlePress, onSelect, handleOnChecked }) => {
	if (selectMode !== "None") {
		const isSelectable = allowedTypes === "Both" || (allowedTypes === "Site" && basicNode.type === "Site") || (allowedTypes === "Group" && basicNode.type === "Group");

		const pressFunction = isSelectable
			? () => {
					if (selectMode !== "Multiple" && onSelect) {
						onSelect(basicNode);
					} else {
						handleOnChecked(basicNode);
					}
				}
			: handlePress;

		return <Pressable onPress={pressFunction}>{children}</Pressable>;
	}

	return <Link href={`/admin/groups/${basicNode.id}`}>{children}</Link>;
};

const TreeGroupNode: React.FC<TreeGroupNodeProps> = ({
	basicNode,
	level,
	defaultOpen,
	render,
	variant = "default",
	isFluid,
	onSelect,
	includeContextMenu = false,
	selectMode = "None",
	allowedTypes = "Both",
	value,
	onMultipleSelect,
	selectedElementRef,
	scrollToElement,
	matchMode = "Site",
	truncateTextSize = null,
	onContextMenuPress = null,
	rootNodeName = "Default Group",
}) => {
	const [isOpen, setIsOpen] = React.useState(defaultOpen);
	const [isSelected, setIsSelected] = useInitialSelectionState(basicNode, value, matchMode);

	const isMobile = useIsMobile();
	const { data: node, isLoading } = useGetGroupQuery(
		{
			selectedNode: basicNode?.id,
			includePermissions: false,
			searchForNode: level === 0 ? (Array.isArray(value) ? null : value?.id) : null,
		},
		{ skip: !isOpen },
	);

	useEffect(() => {
		if (basicNode && value) {
			const valuesArray = Array.isArray(value) ? value : [value];
			const isSelectedInChildren = isSelectedNodeInChildren(
				basicNode,
				valuesArray.map((val) => val.id),
				matchMode,
			);
			if (isSelectedInChildren) {
				setIsOpen(true);
			}
		}
	}, [basicNode, value, matchMode]);

	const handlePress = () => setIsOpen(!isOpen);

	const handleOnChecked = (node: TreeNode) => {
		setIsSelected(!isSelected);
		const updatedNode = { ...node, isSelected: !isSelected };
		if (onMultipleSelect) onMultipleSelect(updatedNode);
	};

	if (basicNode.type === "Site" && allowedTypes === "Group") {
		return null;
	}

	return (
		<Box style={{ paddingLeft: level === 0 ? 0 : 40 }}>
			<Box flexDirection="row" gap={28} alignItems="center">
				<Box
					style={{ width: isFluid ? "100%" : 400 - 40 * level }}
					flexDirection="row"
					bg={level === 0 ? "$darkteal" : "$basicwhite"}
					borderWidth={basicNode.type === "Group" && level !== 0 ? 2 : 0}
					borderColor={level === 0 ? "$basicwhite" : isOpen && basicNode.type === "Group" ? "$neutral500" : "$neutral100"}
					borderRadius={"$full"}
					gap={8}
					mt={8}
					py={8}
					paddingHorizontal={8}
					alignItems="center"
				>
					{basicNode.type === "Site" && (
						<Box width={32}>
							<MapPin color="black" width={32} height={32} />
						</Box>
					)}
					{basicNode.type === "Group" && (
						<Pressable borderRadius={30} bg="$neutral50" onPress={handlePress}>
							<ChevronToggle isOpen={isOpen} />
						</Pressable>
					)}

					<Box flex={1}>
						<NodeTextContainer basicNode={basicNode} selectMode={selectMode} allowedTypes={allowedTypes} handlePress={handlePress} onSelect={onSelect} handleOnChecked={handleOnChecked}>
							<Text color={level === 0 ? "$basicwhite" : ""} fontWeight={isOpen && basicNode.type === "Group" ? "bold" : "normal"} numberOfLines={1} ellipsizeMode="tail">
								{truncateTextSize ? (level === 0 ? rootNodeName : truncateText(basicNode.name ?? "", truncateTextSize)) : basicNode.name}
							</Text>
						</NodeTextContainer>
					</Box>

					{includeContextMenu && (
						<Box ml="auto" paddingRight={12}>
							<AdminContextMenu node={basicNode} level={level} onContextMenuPress={onContextMenuPress} />
						</Box>
					)}

					{selectMode === "Multiple" && (
						<Box ml="auto" paddingRight={12}>
							<CheckBox
								color={"#2B6FBB"}
								value={isSelected}
								onChange={() => {
									setIsSelected(!isSelected);
									handleOnChecked(basicNode);
								}}
							/>
						</Box>
					)}
				</Box>
				{render && render(basicNode)}
			</Box>
			{isOpen ? (
				node ? (
					node?.children?.map((child) => (
						<TreeGroupNode
							key={child.id}
							basicNode={child}
							level={level + 1}
							defaultOpen={false}
							render={render}
							isFluid={isFluid}
							onSelect={onSelect}
							includeContextMenu={includeContextMenu}
							selectMode={selectMode}
							value={value}
							onMultipleSelect={onMultipleSelect}
							selectedElementRef={selectedElementRef}
							scrollToElement={scrollToElement}
							matchMode={matchMode}
							truncateTextSize={truncateTextSize}
							onContextMenuPress={onContextMenuPress}
							allowedTypes={allowedTypes}
						/>
					))
				) : (
					<Box minHeight={isMobile ? 90 : 120}>
						<PageLoader size="sm" />
					</Box>
				)
			) : null}
		</Box>
	);
};

export default TreeGroupNode;
